import { Text, NextImage } from "@sitecore-jss/sitecore-jss-nextjs";
import clsx from "clsx";
import { useMemo, useRef, useLayoutEffect } from "react";
import * as Flex from "@repo/ui/flex";
import {
  TYPOGRAPHY_BODY3,
  TYPOGRAPHY_HEADING3_BOLD,
  TYPOGRAPHY_LABEL3,
  TYPOGRAPHY_HEADING1,
  TYPOGRAPHY_DISPLAY2,
  TYPOGRAPHY_DISPLAY1,
} from "@/typography-constants";
import * as Viewport from "@/viewport";
import { ComponentProps } from "../../../types/component-props";
import AnimatedCtaLink from "../../animated-cta-link/animated-cta-link";
import { appendRef, isMobile } from "../../utils";
import { VideoControls } from "../../video-controls";
import { CategoryShowcase as CategoryShowcaseType } from "../types";
import CategoryShowcaseCarousel from "./category-showcase-carousel";
import styles from "./category-showcase.module.css";

type CategoryShowcaseProps = ComponentProps & {
  fields: { data: { datasource: CategoryShowcaseType } };
};

const CategoryShowcase = ({
  fields: {
    data: {
      datasource: {
        title,
        subTitle,
        carouselHeader,
        ctaText,
        ctaLink,
        ctaRef,
        paragraph,
        heroImage,
        heroVideo,
        enableAutoPlay,
        topics: { results: carouselItems },
      },
    },
  },
}: CategoryShowcaseProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  const isVideo = !!heroVideo.value;
  const hrefWithCtaRef = appendRef(ctaLink.href, ctaRef?.value || null);
  const mobileView = isMobile();

  const shopCategoryLink = useMemo(
    () => (
      <AnimatedCtaLink
        className={clsx(TYPOGRAPHY_LABEL3, styles.headerLink)}
        link={{
          value: { ...ctaLink, text: ctaText.value, href: hrefWithCtaRef },
        }}
      />
    ),
    [ctaLink, ctaText.value, hrefWithCtaRef],
  );

  useLayoutEffect(() => {
    const handleAutoplay = async () => {
      if (videoRef.current && enableAutoPlay?.value && isVideo) {
        try {
          videoRef.current.muted = true;
          await videoRef.current.play();
        } catch (error) {
          console.warn("Autoplay failed:", error);
        }
      }
    };

    const timer = setTimeout(handleAutoplay, 100);
    return () => clearTimeout(timer);
  }, [enableAutoPlay?.value, isVideo, videoRef.current, mobileView]);

  const VideoComponent = () => (
    <>
      <video
        data-testid="showcase-video"
        playsInline
        loop
        muted
        poster={enableAutoPlay?.value ? "" : heroImage.src}
        preload={enableAutoPlay?.value ? "auto" : "metadata"}
        ref={videoRef}
        role="presentation"
        src={heroVideo.value}
        className={styles.video}
      />
      <VideoControls
        videoRef={videoRef}
        color="primary"
        size="sm"
        componentName="feature"
      />
    </>
  );

  return (
    <>
      <Viewport.Small>
        <Flex.Container id={title.value} column columnGap="xl">
          <div className={styles.showcaseVideoContainer}>
            {isVideo ? (
              <VideoComponent />
            ) : (
              <NextImage
                unoptimized
                field={{ value: { ...heroImage } }}
                alt={heroImage.alt}
                className={styles.image}
              />
            )}
            <Flex.Container
              column
              justify="end"
              align="center"
              className={styles.titleContainer}
            >
              <Text
                tag="h1"
                className={clsx(TYPOGRAPHY_DISPLAY1)}
                field={title}
              />
            </Flex.Container>
          </div>

          <Flex.Container
            column
            gap="lg"
            align="center"
            className={styles.contentContainer}
          >
            <Text
              tag="p"
              className={clsx(TYPOGRAPHY_BODY3, styles.description)}
              field={paragraph}
            />

            <CategoryShowcaseCarousel slides={carouselItems} />
            {shopCategoryLink}
          </Flex.Container>
        </Flex.Container>
      </Viewport.Small>
      <Viewport.Large>
        <Flex.Container id={title.value} columnGap="xl">
          <div className={styles.showcaseVideoContainer}>
            {isVideo ? (
              <VideoComponent />
            ) : (
              <NextImage
                unoptimized
                field={{ value: { ...heroImage } }}
                alt={heroImage.alt}
                className={styles.image}
              />
            )}
          </div>
          <Flex.Container
            column
            gap="md"
            align="start"
            className={styles.contentContainer}
          >
            <Flex.Container
              className={styles.headerContainer}
              justify="space-between"
              align="center"
            >
              <Flex.Container gap="md" align="center">
                <Text
                  field={title}
                  tag="p"
                  className={clsx(TYPOGRAPHY_DISPLAY2)}
                />
                <Text
                  field={subTitle}
                  tag="h1"
                  className={clsx(TYPOGRAPHY_HEADING1)}
                />
              </Flex.Container>
              {shopCategoryLink}
            </Flex.Container>
            <Text
              tag="p"
              className={clsx(TYPOGRAPHY_BODY3)}
              field={paragraph}
            />

            <Text
              tag="h3"
              field={carouselHeader}
              className={clsx(TYPOGRAPHY_HEADING3_BOLD)}
            />
            <CategoryShowcaseCarousel slides={carouselItems} />
          </Flex.Container>
        </Flex.Container>
      </Viewport.Large>
    </>
  );
};

export default CategoryShowcase;
